<!-- @format -->
<template>
	<div :class="['center-main-container', isMobile ? 'center-main-container-mobile' : '']">
		<template v-if="widgetDatas.length === 0">
			<el-empty class="not-content" description="请从左侧拖拽字段" />
		</template>
		<el-form
			:class="[
				'center-main-container-form',
				formAttribute.labelEllipsis ? 'center-main-container-form-ellipsis-label' : '',
			]"
			v-bind="formAttribute"
			:label-width="labelWidth"
			@submit.prevent
		>
			<widget-form
				v-model:widget-datas="widgetDatas"
				:form-datas="formDatas"
				:is-field-to-container="isFieldToContainer"
				:is-container-to-container="isContainerToContainer"
				:can-delete="canDelete"
				:can-sort="canSort"
				:can-drag-in="canDragIn"
				:init-code="initCode"
				:is-mobile="isMobile"
				@on-delete="emit('on-delete', $event)"
				@on-select-callback="emit('on-select-callback')"
			/>
		</el-form>
	</div>
</template>

<script setup lang="ts">
	import { useDragStoreWithOut } from '../../store';
	import { getDefaultValue, transWidgetDatas } from '../../utils/transWidgetDatas';
	import WidgetForm from './widget-form.vue';
	import type { FieldsType } from '../../types';
	import { useDeferSetArray } from '@/utils/defer';

	interface Props {
		defaultWidgetDatas?: FieldsType[];
		defaultFormAttribute?: Record<string, Function>;
		isFieldToContainer?: boolean;
		isContainerToContainer?: boolean;
		canDelete?: boolean | ((a: any) => boolean);
		canSort?: boolean | ((a: any) => boolean);
		canDragIn?: boolean | ((a: any) => boolean);
		name?: string;
		initCode?: boolean | ((a: any) => string);
		isMobile?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		canDelete: true,
		canSort: true,
		canDragIn: true,
		initCode: true,
	});

	const emit = defineEmits(['on-delete', 'on-select-callback']);

	const dragStore = useDragStoreWithOut(props.name);

	const widgetDatas = computed({
		get() {
			return dragStore.widgetDatas;
		},
		set(val) {
			dragStore.setState({ widgetDatas: val });
		},
	});

	const formDatas = computed(() => getDefaultValue(widgetDatas.value, {}, []));

	const formAttribute = computed(() => dragStore.formAttribute);

	const labelWidth = computed(() =>
		formAttribute.value.labelWidth && formAttribute.value.labelPosition !== 'top'
			? `${formAttribute.value.labelWidth}px`
			: 'auto',
	);

	const labelPosition = computed(() => (formAttribute.value.labelPosition === 'right' ? 'end' : 'start'));

	const { cancel, setDeferArray } = useDeferSetArray();

	watch(
		[() => props.defaultWidgetDatas, () => props.defaultFormAttribute],
		([defaultWidgetdatas, defaultFormAttribute]) => {
			cancel();
			defaultWidgetdatas &&
				setDeferArray(widgetDatas, transWidgetDatas(defaultWidgetdatas, props.name), { deep: true });
			defaultFormAttribute && dragStore.setState({ formAttribute: defaultFormAttribute });
		},
		{
			immediate: true,
		},
	);

	onUnmounted(() => {
		cancel();
	});

	// 表单状态，widget | render | preview
	provide('form-status', 'widget');

	// store唯一值
	provide('name', props.name);

	// 默认表单表单数据
	provide('formDatas', formDatas);

	// 表单配置
	provide('formAttribute', formAttribute);

	defineExpose({ widgetDatas, formAttribute });
</script>

<style scoped lang="scss">
	.center-main-container {
		height: 100%;
		width: 100%;
		position: relative;

		.not-content {
			height: 100%;
			width: 100%;
			position: absolute;
		}

		.center-main-container-form {
			height: 100%;
			overflow-y: scroll;
		}

		.center-main-container-form-ellipsis-label {
			:deep(.el-form-item) {
				.el-form-item__label_val {
					display: inline-block;
					text-align: v-bind(labelPosition);
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
			}
		}
	}

	.center-main-container-mobile {
		margin: 0 auto;
		width: 420px;
		border-radius: 15px;
		box-shadow: 0 0 1px 10px #495060;
		padding: 5px;
	}
</style>

<style lang="scss">
	.center-main-container {
		.widget-form {
			@for $i from 1 through 24 {
				.fields-item-#{$i} {
					max-width: percentage(calc($i / 24));
					flex: 0 0 percentage(calc($i / 24));
				}
			}

			.ghost {
				content: '';
				font-size: 0;
				height: 5px;
				box-sizing: border-box;
				background: var(--el-color-primary);
				border: 2px solid var(--el-color-primary);
				outline-width: 0;
				padding: 0;
				overflow: hidden;
			}

			// 解决容器带border导致字段移入时经过边框导致字段抖动问题，字段移到边框时draggable会认为是移入当前字段上方，所以产生抖动，去掉边框该用outline即可
			.el-collapse-item__wrap {
				border-bottom: none;
			}
		}
	}
</style>
